import React from "react";
import { motion } from "framer-motion";
import { Quote, Target, Compass, Gem, Instagram } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/brand";

const VALUES = [
  { icon: Target, title: "Missão", text: "Impulsionar, organizar e ajudar empresas a venderem mais todos os dias." },
  { icon: Compass, title: "Visão", text: "Ser referência nacional em vendas e gestão empresarial." },
  { icon: Gem, title: "Valores", text: "Excelência, Melhoria Contínua, Integridade, Trabalho em Equipe, Prosperidade, Empatia, Anti Fragilidade, Comprometimento, Vendas, Resultados e Ambiência." },
];

export default function AuthoritySection() {
  return (
    <section id="sobre" className="scroll-mt-nav bg-efory-ink text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-3xl bg-efory-blue/10 -rotate-2" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-xl">
              <img
                src={IMAGES.author}
                alt={BRAND.founder}
                className="w-full h-auto object-contain"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-heading text-xl font-bold">{BRAND.founder}</p>
                <a
                  href={BRAND.founderInstagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="author-instagram"
                  className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm mt-1 transition-colors"
                >
                  <Instagram className="h-4 w-4" /> @bernardohenriqs
                </a>
              </div>
            </div>
          </motion.div>

          {/* Authority text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block rounded-full border border-efory-blue/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
              Autoridade & Propósito
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-6 leading-[1.1]">
              Quem está por trás da <span className="text-efory-blue">EFORY</span>
            </h2>

            <div className="mt-6 relative pl-6 border-l-2 border-efory-blue">
              <Quote className="h-8 w-8 text-efory-blue/30 absolute -left-1.5 -top-3" />
              <p className="font-display italic text-3xl sm:text-4xl font-bold text-white leading-tight">
                Prazer, sou Bernardo Henriques!
              </p>
              <p className="mt-4 font-display italic text-xl sm:text-2xl text-white/85 leading-relaxed">
                Empresário e{" "}
                <span className="underline decoration-efory-blue decoration-2 underline-offset-4 text-white">
                  consultor empresarial
                </span>
                ,{" "}
                <span className="underline decoration-efory-blue decoration-2 underline-offset-4 text-white">
                  fundador do Grupo EFORY
                </span>
                , especialista em{" "}
                <span className="underline decoration-efory-blue decoration-2 underline-offset-4 text-white">
                  vendas, gestão comercial e crescimento de negócios
                </span>
                .
              </p>
            </div>

            <div className="mt-6 space-y-4 text-white/65 leading-relaxed">
              <p>
                Ao longo da minha trajetória, percebi que muitos negócios têm bons produtos, bons
                serviços e grande potencial, mas acabam travados por falta de clareza comercial,
                gestão, acompanhamento e previsibilidade.
              </p>
              <p>
                Sou formado em Gestão Comercial, com especializações em Sucesso do Cliente e Gestão
                Avançada de Pessoas, e atuo diretamente com negócios que têm potencial de
                crescimento, mas vivem travados por falta de processo comercial, acompanhamento,
                gestão e direção estratégica.
              </p>
              <p>
                Meu método já ajudou{" "}
                <span className="text-white font-semibold">mais de 200 empresas</span> a
                estruturarem suas vendas, melhorarem sua gestão e construírem uma operação mais
                organizada, lucrativa e preparada para crescer.
              </p>
            </div>

            <div className="mt-9 grid sm:grid-cols-3 gap-4">
              {VALUES.map((v) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.title}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-efory-blue/40 hover:bg-white/[0.05] transition-all duration-300"
                  >
                    <div className="h-10 w-10 rounded-lg bg-efory-blue/10 flex items-center justify-center text-efory-blue">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-heading font-bold mt-3">{v.title}</h4>
                    <p className="text-sm text-white/60 mt-1.5 leading-relaxed">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
